import { computed, type ComputedRef } from 'vue'
import { useRoute } from 'vue-router'

import { useCartStore } from '@/stores/cart'
import { useCatalogStore } from '@/stores/catalog'

import type { CatalogBundleStorefrontDtoWithContent } from '@/types/CatalogBundleStorefrontDtoWithContent'

// Max Quick Adds shown in the flyout. The ceiling is an open refinement (Black Airplane doc) — see
// guide §4.4; kept here as a single tunable constant.
const QUICK_ADD_LIMIT = 6

export interface UseQuickAdds {
  /** Cross-sell bundles to show as Quick Adds, ordered + capped for the flyout. */
  quickAddBundles: ComputedRef<CatalogBundleStorefrontDtoWithContent[]>
}

/**
 * Cart-flyout Quick Adds (YMAL) selection (guide §4.4, Brian 2026-06-11): from the cross-sell pool
 * (`crossSellCatalogBundles`, already proof-type-scoped), drop bundles already in the cart, prefer
 * those whose price lands closest to the next reward tier's gap, then present low→high, capped at
 * `QUICK_ADD_LIMIT`. The N ceiling and the exact "closest to gap" rule are open refinements pending
 * the Black Airplane doc; this is the day-1 heuristic.
 *
 * Suppressed entirely on routes flagged `meta.suppressCartFlyoutQuickAdds` (the Checkout route): YMAL is a
 * cart-mutating add, and we don't want new cart operations on the final review/pay step where they
 * risk total/payment desync and the jarring route-back-to-Select-Poses end-around (see
 * `QuickAddsFlyout`). This is the rewards-scoped guard; a fully read-only checkout cart is a larger,
 * separate decision (dev + business).
 */
export function useQuickAdds(): UseQuickAdds {
  const catalogStore = useCatalogStore()
  const cartStore = useCartStore()
  const route = useRoute()

  const quickAddBundles = computed(() => {
    // No YMAL where the route opts out (Checkout) — no cart operations on the final review/pay step.
    if (route.meta.suppressCartFlyoutQuickAdds) {
      return []
    }

    // Dollars to the next reward tier (`0` when there is no next tier).
    const gap = catalogStore.nextRewardTier(cartStore.effectiveSubtotal)?.amountToUnlock ?? 0
    const price = (bundle: CatalogBundleStorefrontDtoWithContent): number =>
      bundle.pricing?.basePrice ?? 0

    const available = catalogStore.crossSellCatalogBundles.filter(
      (cb) => !cartStore.catalogBundleIsInCart(cb.catalogBundleId)
    )

    // Rank by how well a bundle gets the customer *over* the reward gap (when there is one); cap N.
    // This is also the display order. Bundles that meet-or-exceed the gap rank first — those actually
    // unlock the reward — ordered cheapest-first (the smallest add that crosses the line). Shortfall
    // bundles follow, ordered closest-to-the-line first (priciest of the unders). So overshooting beats
    // undershooting rather than tying it by raw distance. Price-ascending is the fallback when there's
    // no gap (all tiers unlocked / none configured). `available` is already a fresh array, so sort it
    // in place.
    return available
      .sort((a, b) => {
        if (gap > 0) {
          const aMeetsGap = price(a) >= gap
          const bMeetsGap = price(b) >= gap
          if (aMeetsGap !== bMeetsGap) return aMeetsGap ? -1 : 1 // crossing bundles first
          if (aMeetsGap) return price(a) - price(b) // cheapest that crosses the line first
          return price(b) - price(a) // among shortfalls, closest to the line first
        }
        return price(a) - price(b)
      })
      .slice(0, QUICK_ADD_LIMIT)
  })

  return { quickAddBundles }
}
